import {
  AddProjectMemberData,
  ApiResponse,
  CreateProjectData,
  Project,
  ProjectListParams,
  ProjectListResponse,
  ProjectMember,
  UpdateProjectMemberData,
} from '../types';
import { apiService } from './api';
import { GeocodeRequest, locationService } from './locationService';

// Helper function to geocode project location
const geocodeProjectLocation = async (
  projectData: CreateProjectData | Partial<CreateProjectData>
): Promise<CreateProjectData | Partial<CreateProjectData>> => {
  // Only geocode if we have location data and Google Maps is available
  if (!projectData.location || !locationService.isGoogleMapsAvailable()) {
    return projectData;
  }

  const { location } = projectData;

  // Check if we have enough location data to geocode
  if (!location.address || !location.city || !location.country) {
    return projectData;
  }

  try {
    const geocodeRequest: GeocodeRequest = {
      address: location.address,
      city: location.city,
      state: location.state || '',
      country: location.country,
      zipCode: location.zipCode,
    };

    console.log('Geocoding address:', geocodeRequest);
    const geocodeResult = await locationService.geocodeAddress(geocodeRequest);

    if (geocodeResult) {
      console.log('Geocoding successful:', geocodeResult);

      // Add coordinates to the project data
      return {
        ...projectData,
        location: {
          ...location,
          coordinates: geocodeResult.coordinates,
        },
      };
    } else {
      console.warn('Geocoding failed for address:', geocodeRequest);
    }
  } catch (error) {
    console.error('Error during geocoding:', error);
  }

  return projectData;
};

export const projectService = {
  // Get all projects with advanced filtering
  getProjects: async (params?: ProjectListParams): Promise<ApiResponse<ProjectListResponse>> => {
    try {
      const queryParams = new URLSearchParams();

      // Add all possible parameters
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search && params.search.trim().length >= 2)
        queryParams.append('search', params.search);
      if (params?.status && params.status !== 'all') queryParams.append('status', params.status);
      if (params?.priority && params.priority !== 'all')
        queryParams.append('priority', params.priority);
      if (params?.location) queryParams.append('location', params.location);
      if (params?.managerId) queryParams.append('managerId', params.managerId);
      if (params?.teamMember) queryParams.append('teamMember', params.teamMember);
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
      if (params?.dateTo) queryParams.append('dateTo', params.dateTo);
      if (params?.budgetMin !== undefined)
        queryParams.append('budgetMin', params.budgetMin.toString());
      if (params?.budgetMax !== undefined)
        queryParams.append('budgetMax', params.budgetMax.toString());

      // Handle tags array
      if (params?.tags && params.tags.length > 0) {
        params.tags.forEach(tag => queryParams.append('tags', tag));
      }

      const url = `/projects${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      return await apiService.get<ProjectListResponse>(url);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch projects',
      };
    }
  },

  // Get project by ID
  getProject: async (id: string): Promise<ApiResponse<Project>> => {
    try {
      return await apiService.get<Project>(`/projects/${id}`);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch project',
      };
    }
  },

  // Create new project
  createProject: async (projectData: CreateProjectData): Promise<ApiResponse<Project>> => {
    try {
      // Geocode the address to get lat/lng coordinates
      const geocodedData = await geocodeProjectLocation(projectData);

      return await apiService.post<Project>('/projects', geocodedData);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create project',
        errors: error.response?.data?.errors,
      };
    }
  },

  // Update project
  updateProject: async (
    id: string,
    projectData: Partial<CreateProjectData>
  ): Promise<ApiResponse<Project>> => {
    try {
      // Geocode the address if location data is being updated
      const geocodedData = await geocodeProjectLocation(projectData);

      return await apiService.put<Project>(`/projects/${id}`, geocodedData);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update project',
        errors: error.response?.data?.errors,
      };
    }
  },

  // Delete project
  deleteProject: async (id: string): Promise<ApiResponse<void>> => {
    try {
      return await apiService.delete<void>(`/projects/${id}`);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete project',
      };
    }
  },

  // Get project statistics
  getProjectStats: async (): Promise<ApiResponse<any>> => {
    try {
      return await apiService.get<any>('/projects/stats');
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch project statistics',
      };
    }
  },

  // Get project dashboard data
  getProjectDashboard: async (id: string): Promise<ApiResponse<any>> => {
    try {
      return await apiService.get<any>(`/projects/${id}/dashboard`);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch project dashboard',
      };
    }
  },

  // Get comprehensive project overview data
  getProjectOverview: async (id: string): Promise<ApiResponse<any>> => {
    try {
      const response = await apiService.get<any>(`/projects/${id}/overview`);
      return response;
    } catch (error: any) {
      // Handle different types of errors
      let errorMessage = 'Failed to fetch project overview';

      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
        errorMessage = 'Unable to connect to server. Please check your connection.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timeout. Please try again.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication required. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. You do not have permission to view this project.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Project not found.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      return {
        success: false,
        message: errorMessage,
      };
    }
  },

  // Team Management Methods

  // Get project team members
  getProjectTeam: async (
    projectId: string,
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      role?: string;
      status?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      includeInactive?: boolean;
    }
  ): Promise<ApiResponse<any>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.role) queryParams.append('role', params.role);
      if (params?.status) queryParams.append('status', params.status);
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params?.includeInactive)
        queryParams.append('includeInactive', params.includeInactive.toString());

      const url = `/projects/${projectId}/team${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      return await apiService.get<any>(url);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch team members',
      };
    }
  },

  // Add team member to project
  addTeamMember: async (
    projectId: string,
    data: AddProjectMemberData
  ): Promise<ApiResponse<ProjectMember>> => {
    try {
      return await apiService.post<ProjectMember>(`/projects/${projectId}/team`, data);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to add team member',
        errors: error.response?.data?.errors,
      };
    }
  },

  // Update team member
  updateTeamMember: async (
    projectId: string,
    memberId: string,
    data: UpdateProjectMemberData
  ): Promise<ApiResponse<ProjectMember>> => {
    try {
      return await apiService.put<ProjectMember>(`/projects/${projectId}/team/${memberId}`, data);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update team member',
        errors: error.response?.data?.errors,
      };
    }
  },

  // Remove team member from project
  removeTeamMember: async (projectId: string, memberId: string): Promise<ApiResponse<void>> => {
    try {
      return await apiService.delete<void>(`/projects/${projectId}/team/${memberId}`);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to remove team member',
      };
    }
  },

  // Get available users for team assignment
  getAvailableUsers: async (projectId: string, search?: string): Promise<ApiResponse<any[]>> => {
    try {
      const queryParams = new URLSearchParams();
      if (search) queryParams.append('search', search);

      const url = `/projects/${projectId}/available-users${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      return await apiService.get<any[]>(url);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch available users',
      };
    }
  },

  // Bulk operations
  bulkAction: async (projectIds: string[], action: string): Promise<ApiResponse<any>> => {
    try {
      return await apiService.post<any>('/projects/bulk-action', {
        projectIds,
        action,
      });
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to perform bulk action',
      };
    }
  },
};

export default projectService;
